import * as React from "react"

import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"
import { Etiqueta } from "@/components/ui/etiqueta"
import { obtenerIniciales } from "@/lib/iniciales"
import { cn } from "@/lib/utils"

interface PersonaDelEquipo {
  id: number
  nombre: string
}

interface MiembroDelEquipo {
  /** El rótulo del rol: "Ejecutivo a cargo". */
  rol: string
  persona: PersonaDelEquipo | null
  /** El rol que tiene hoy el informe en las manos. A lo más uno. */
  leToca?: boolean
}

/* Un color suave por persona, estable: sale del id y de los tonos soft del DS
   (nunca de un hex), así acompaña al modo oscuro. Se escriben completas para que
   Tailwind las encuentre. */
const COLORES = [
  "bg-success-soft text-success-soft-foreground",
  "bg-info-soft text-info-soft-foreground",
  "bg-warning-soft text-warning-texto",
  "bg-divisor text-ink-secundario",
  "bg-desactualizada-soft text-desactualizada-foreground",
]

function colorDePersona(id: number): string {
  return COLORES[Math.abs(id) % COLORES.length]!
}

/** El círculo de iniciales con el color de la persona. */
function AvatarDePersona({
  persona,
  className,
  ...props
}: React.ComponentProps<"span"> & { persona: PersonaDelEquipo }) {
  return (
    <span
      data-slot="pila-de-avatares-avatar"
      title={persona.nombre}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-extrabold",
        colorDePersona(persona.id),
        className
      )}
      {...props}
    >
      {obtenerIniciales(persona.nombre)}
    </span>
  )
}

/**
 * Pila de avatares del EQUIPO de un informe: las personas únicas entre los roles,
 * de a 30px con -8px de solape y un anillo blanco; la que tiene el turno va
 * primera y con un anillo verde de 4px. Al apuntarla —o enfocarla con Tab— se
 * abre una tarjeta con los roles, quién los ocupa y cuál "Le toca".
 *
 * El disparador es un botón para que el teclado lo alcance: sin eso la tarjeta
 * sería solo para quien tiene mouse.
 */
function PilaDeAvatares({
  equipo,
  nombre = "Equipo",
  className,
}: {
  equipo: MiembroDelEquipo[]
  /** El nombre accesible del disparador. */
  nombre?: string
  className?: string
}) {
  const turno = equipo.find((m) => m.leToca && m.persona)?.persona?.id
  const unicas: PersonaDelEquipo[] = []
  for (const { persona } of equipo) {
    if (persona && !unicas.some((p) => p.id === persona.id)) unicas.push(persona)
  }
  unicas.sort((a, b) => Number(b.id === turno) - Number(a.id === turno))

  if (unicas.length === 0) {
    return <span className="text-ink-hint text-xs font-semibold">Sin asignar</span>
  }

  return (
    <HoverCard openDelay={100} closeDelay={100}>
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-slot="pila-de-avatares"
          aria-label={`${nombre}: ${unicas.map((p) => p.nombre).join(", ")}`}
          className={cn(
            "focus-visible:ring-ring/50 flex items-center rounded-full outline-none focus-visible:ring-[3px]",
            className
          )}
        >
          {unicas.map((persona, i) => {
            const leToca = persona.id === turno
            return (
              <AvatarDePersona
                key={persona.id}
                persona={persona}
                data-le-toca={leToca ? "true" : undefined}
                style={{ zIndex: unicas.length - i }}
                className={cn(
                  "relative size-[1.875rem] text-[0.65625rem]",
                  i > 0 && "-ml-2",
                  leToca
                    ? "shadow-[0_0_0_2px_var(--card),0_0_0_4px_var(--primary)]"
                    : "shadow-[0_0_0_2px_var(--card)]"
                )}
              />
            )
          })}
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        role="dialog"
        aria-label={`${nombre}: roles`}
        align="end"
        className="bg-card flex w-[18rem] flex-col gap-2 rounded-[0.875rem] p-3"
      >
        {equipo.map(({ rol, persona, leToca }) => (
          <div key={rol} className="flex items-center gap-2.5">
            {persona ? (
              <AvatarDePersona persona={persona} className="size-6 text-[0.59375rem]" />
            ) : (
              <span className="bg-divisor size-6 shrink-0 rounded-full" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-ink-medio text-[0.65625rem] font-bold">{rol}</div>
              <div
                className={cn(
                  "truncate text-xs font-bold",
                  persona ? "text-ink" : "text-ink-hint font-semibold"
                )}
              >
                {persona?.nombre ?? "Sin asignar"}
              </div>
            </div>
            {leToca && persona && (
              <Etiqueta tono="success" size="sm">
                Le toca
              </Etiqueta>
            )}
          </div>
        ))}
      </HoverCardContent>
    </HoverCard>
  )
}

export { PilaDeAvatares, AvatarDePersona, colorDePersona }
export type { MiembroDelEquipo, PersonaDelEquipo }
